import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { errorToast } from 'configuration/notifications';
import { clientApi } from 'helpers/api/client';

import { Stock } from 'store/stock/actions';
import { RentContinue as RentContinueActionService, Rent } from 'store/transferAct/actions';
import { rentContinueSelector, rentSelector, rentInitalSelector } from 'store/transferAct/selectors';
import { stockListSelector, stockSelector } from 'store/stock/selectors';

import { Header } from 'components/Header';
import { ButtonIcon, ButtonLabel } from 'components/Button';
import { Loader } from 'components/Loader';

import { ModalAttention } from 'components/ModalAttention';
import { ActionButtonsSection } from './components/ActionButtonsSection';
import { InfoCardsContinueSection } from './components/InfoCardsContinueSection';
import { RentContinueTable } from './components/RentContinueTable';
import './style.scss';

const RentContinue = () => {
  const { rentId } = useParams();

  const history = useHistory();
  const dispatch = useDispatch();

  const rent = useSelector(rentContinueSelector);

  const stock = useSelector(stockSelector);
  const stockList = useSelector(stockListSelector);
  const initalRent = useSelector(rentInitalSelector);

  const [historyModal, setHistoryModal] = useState(null);
  const [attentionModal, setAttentionModal] = useState(false);
  const [isFullPledge, setIsFullPledge] = useState(false);

  useEffect(() => {
    if (!rent) {
      dispatch(RentContinueActionService.getRent(rentId));
    }
    dispatch(Rent.getRent(rentId));
  }, []);

  useEffect(() => {
    if (rent?.actID1C && stockList) {
      const findStock = stockList.find((item) => item.stockID1C === rent.stockID);

      if (!findStock) {
        errorToast('У Вас нет прав для редактирования этого склада!');
        history.push('/');
        return;
      }

      if (stock?.stockID1C !== findStock?.stockID1C) {
        dispatch(Stock.setStock(findStock));
      }
    }
  }, [rent]);

  const itemsSurchargeAmountSum = useCallback(() => {
    if (!rent?.instruments) {
      return 0;
    }

    let sum = rent.instruments.reduce((acc, cur) => acc + cur.AdditionalRentTerm_amount * cur.quantity, 0);
    sum += rent.fine.reduce((acc, n) => acc + Number(n.sum), 0);
    sum -= rent.discount.reduce((acc, n) => acc + Number(n.sum), 0);
    return sum;
  }, [rent]);

  const updateRent = (data) => {
    dispatch(RentContinueActionService.updateRent(data));
  };

  const handleBack = () => {
    dispatch(RentContinueActionService.clearRent());
    history.push(`/rent/${rentId}`);
  };

  const handleClose = (isSubmit) => {
    if (!isSubmit && JSON.stringify(initalRent) !== JSON.stringify(rent)) {
      setAttentionModal(true);
      return;
    }
    dispatch(RentContinueActionService.clearRent());
    toggleAttentionModal(false);
    history.push('/');
  };

  const toggleAttentionModal = (toggle) => {
    setAttentionModal(toggle);
  };

  const handleSave = async () => {
    const client = rent.client && (await clientApi.getClient(rent.client?.id));
    if (client.isTemporary && !isFullPledge) {
      return errorToast('Клиент не полностью заполнен. Отправка данных в 1C невозможна');
    }
    history.push(`/rent/${rentId}/checkout/continue`);
  };

  const handleClickOutside = () => {
    if (historyModal) {
      setHistoryModal(null);
    }
  };

  const handleUpdateTable = (newData) => {
    let updatedRent = {
      instruments: [
        ...newData.map((elem) => {
          return { ...elem, AdditionalRentTerm: newData[0].AdditionalRentTerm };
        }),
        ...rent.instruments.filter((elem) => elem.subtype  !== 'Инструмент для оренди'),
      ],
    };
    if (newData.length > 0) {
      updatedRent['AdditionalRentTerm'] = newData[0].AdditionalRentTerm;
    }
    updateRent({ ...updatedRent });
  };

  const handleRemoveTable = (instrumentId) => {
    const findIndex = rent.instruments.findIndex((inst) => inst.id === instrumentId);

    if (findIndex !== -1) {
      const newArray = rent.instruments.slice();
      newArray.splice(findIndex, 1);

      updateRent({ instruments: newArray });
    }
  };

  if (!rent) {
    return <Loader />;
  }

  return (
    <>
      <Header title="Продление аренды" onClose={() => handleClose(false)}></Header>

      <main className="rent-main" onClick={handleClickOutside}>
        <ActionButtonsSection />
        <RentContinueTable
          rateVAT={rent?.agr?.organization?.rateVAT || 0}
          canChange={true}
          instrumentsList={rent.instruments}
          onUpdate={handleUpdateTable}
          onRemove={handleRemoveTable}
          isFullPledge={isFullPledge}
          setIsFullPledge={setIsFullPledge}
        />

        <InfoCardsContinueSection rent={rent} stock={stock} showEmpty={true} />

        <div className="rent-maintext">
          Cумма доплаты: <b>{itemsSurchargeAmountSum()} грн.</b>
        </div>

        <div className="buttons-block-grid">
          <div style={{ display: 'flex' }}>
            <ButtonIcon className="btn-main" onClick={handleBack} icon="arrow-left" text="Вернуться к аренде" />
          </div>
          <div className="flex-center">
            <ButtonIcon className="btn-main btn-save" onClick={handleSave} icon="save" text="Продолжить" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonIcon className="btn-main" icon="coin" text="Кассовые ордера" onClick={handleSave} />
          </div>
        </div>
      </main>

      {attentionModal && (
        <ModalAttention onClose={() => toggleAttentionModal(false)} onSubmit={() => handleClose(true)} />
      )}
    </>
  );
};

export { RentContinue };
